import type { SVGProps } from "react";

/**
 * Kit de marca do myGROW.
 *
 * O kit entrega lockup e símbolo como quatro SVGs — onDark/onLight de cada — que
 * diferem num único fill: a camada de contraste é #ffffff sobre navy e #08254c
 * sobre claro. Aqui ela é `currentColor`, então um componente cobre os dois casos
 * e acompanha o contexto (shell navy, hero escuro, tema claro) sem troca de arquivo.
 *
 * Símbolo e lockup compartilham o mesmo traçado: o que muda é o viewBox, que
 * recorta o desenho no símbolo — mesma construção que o kit usa ao exportar.
 */

/** Âmbar da marca. Não é o `--accent` da UI (#ffc83d): o logo tem o âmbar dele. */
const BRAND_AMBER = "#FAB325";

/** Estrela, brilhos e a palavra "GROW". */
const AMBER_LAYER =
  "M243 14L246 15L251 25L256 39L262 49L262 52L268 62L268 65L273 73L273 76L279 86L279 89L285 99L285 102L290 110L290 113L296 123L296 126L317 170L323 175L335 180L336 182L359 193L360 195L370 199L371 201L381 205L382 207L392 211L393 213L403 217L404 219L414 223L415 225L425 229L426 231L460 248L461 250L470 254L470 256L315 327L308 335L304 345L302 346L291 369L289 370L285 380L283 381L279 391L277 392L273 402L271 403L267 413L265 414L261 424L259 425L255 435L253 436L249 446L247 447L242 459L240 460L236 470L234 471L230 479L285 202L12 238L5 237L158 167L164 160L175 137L177 136L181 126L183 125L194 102L196 101L200 91L202 90L206 80L208 79L212 69L214 68L218 58L220 57L224 47L226 46L230 36L232 35L243 14Z M387 14L389 14L404 48L437 65L437 67L428 70L420 75L417 75L407 81L404 81L385 115L370 81L336 62L370 47L387 14Z M437 122L444 134L447 143L470 157L448 167L437 188L435 189L425 166L403 154L425 144L437 122Z M1233 144L1253 144L1268 147L1284 154L1289 159L1291 159L1302 170L1302 172L1308 179L1314 192L1318 208L1319 234L1317 249L1312 265L1305 278L1290 294L1288 294L1281 300L1270 305L1252 309L1223 308L1205 302L1204 300L1194 295L1190 290L1186 288L1186 286L1177 276L1168 255L1165 238L1165 218L1167 205L1172 189L1179 176L1191 162L1193 162L1202 154L1218 147L1233 144Z M942 145L977 146L999 152L992 182L974 176L945 175L930 179L918 186L908 198L903 211L902 238L905 249L913 263L927 274L945 279L967 278L970 276L970 244L945 243L945 215L1005 215L1004 300L965 308L936 308L923 306L907 301L894 294L879 280L871 267L864 243L864 215L869 195L875 183L893 163L903 158L904 156L921 149L942 145Z M1073 145L1101 146L1122 151L1135 159L1141 166L1145 174L1147 181L1147 200L1145 207L1140 216L1131 225L1123 230L1120 230L1120 232L1125 234L1132 241L1138 252L1153 306L1116 306L1112 297L1102 257L1097 249L1087 244L1069 244L1069 306L1033 306L1033 149L1041 147L1073 145Z M1329 147L1368 147L1389 268L1413 147L1452 147L1468 229L1473 266L1495 148L1533 147L1492 306L1452 306L1436 226L1431 190L1407 306L1366 306L1329 147Z M1078 172L1069 173L1069 218L1084 218L1096 216L1105 211L1110 204L1110 185L1104 177L1098 174L1078 172Z M1236 173L1226 176L1221 181L1219 181L1210 193L1204 212L1204 242L1212 264L1220 273L1228 278L1235 280L1250 280L1264 273L1274 260L1280 240L1280 213L1276 198L1272 190L1261 178L1249 173L1236 173Z M3 238L4 239L3 238Z M230 479L229 480L230 479Z";

/** A palavra "my" e a dobra interna da estrela — a camada que muda com o fundo. */
const CONTRAST_LAYER =
  "M612 188L626 189L636 193L644 200L648 209L650 209L654 202L661 196L676 189L697 189L713 198L722 213L725 225L725 306L697 306L696 230L692 220L686 214L680 212L673 212L665 215L658 222L655 229L654 306L626 306L626 233L621 219L617 215L610 212L602 212L595 215L588 222L584 231L584 306L555 306L554 191L579 191L581 208L583 204L594 194L602 190L612 188Z M739 191L771 191L793 255L798 275L822 192L854 191L821 283L802 324L785 345L783 345L777 351L765 357L757 359L754 359L753 357L748 334L763 328L776 316L782 306L782 299L780 297L779 291L777 289L739 191Z M260 222L262 222L262 228L255 257L217 452L215 452L156 323L146 316L88 286L87 284L77 280L76 278L66 274L65 272L55 268L54 266L44 262L43 260L35 257L30 253L260 222Z";

const LOCKUP_VIEWBOX = "3 13 1530 468";
const ICON_VIEWBOX = "3 13 468 468";

export interface MygrowMarkProps
  extends Omit<SVGProps<SVGSVGElement>, "viewBox" | "children"> {
  /**
   * Nome acessível da marca. Passe `null` quando o logo já vier acompanhado do
   * nome em texto, para o leitor de tela não anunciar "myGROW" duas vezes.
   */
  title?: string | null;
}

function MygrowMark({
  viewBox,
  title = "myGROW",
  ...props
}: MygrowMarkProps & { viewBox: string }) {
  const named = typeof title === "string" && title.length > 0;

  return (
    <svg
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      {...(named
        ? { role: "img", "aria-label": title }
        : { "aria-hidden": true })}
      {...props}
    >
      <path fill={BRAND_AMBER} fillRule="nonzero" d={AMBER_LAYER} />
      <path fill="currentColor" fillRule="nonzero" d={CONTRAST_LAYER} />
    </svg>
  );
}

/**
 * Lockup horizontal. Mínimo do kit: 120px de largura (~37px de altura).
 * Abaixo disso use {@link MygrowIcon}.
 */
export function MygrowLogo(props: MygrowMarkProps) {
  return <MygrowMark viewBox={LOCKUP_VIEWBOX} {...props} />;
}

/** Símbolo isolado, para chrome denso e favicon. Mínimo do kit: 24px. */
export function MygrowIcon(props: MygrowMarkProps) {
  return <MygrowMark viewBox={ICON_VIEWBOX} {...props} />;
}
